import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CopyButton, Drawer, EditIcon, EyeIcon, Field, Modal, Select, TrashIcon, useConfirm, useToast } from "@signet/ui";
import { useCreatorPhotos, useMe, type CreatorPhotos } from "@signet/web-kit";
import { CreatorChip } from "../shared.tsx";
import { banLine, isThreeReason, StatusButtons } from "./status.tsx";
import { MissingAlert, TypeBadge, TypeChip } from "./cards.tsx";
import { AppealPanel } from "./appeal.tsx";
import { AccountNote, RemovedPrompt } from "./notes.tsx";
import { classLabel, STOCK, deleteStock, fetchCode, fetchStockCreators, saveCreator, fetchLogin, fetchPhonesOf, phoneLabel, pfpLabel, releaseStock, saveLogin, shortDate, stepStock, stockKeys, takeStock, TYPE_LABEL, type Ban, type Phone, type Poster, type Stock, type Login, type StepName } from "./api.ts";

/* ──────────────────────────────────────────────────────────────────────────
   One account: taking it onto a phone, its step button, and its panel with
   the login, the live 2FA code, every date, and the status buttons.
   ────────────────────────────────────────────────────────────────────────── */

const messageOf = (error: unknown) => (error instanceof Error ? error.message : "Something went wrong.");

/** The one button for the step the account is on, or nothing when there is nothing to tap. */
// Leaving a ban or Not posting is the status buttons' job now (status.tsx).
export const PRIMARY: Partial<Record<Stock["step"]["stage"], { step: StepName; label: string }>> = {
  linked: { step: "link_flagged", label: "Link flagged" },
  flagged: { step: "highlight", label: "Moved it to a highlight" },
};

export function useStep() {
  const client = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, step, ban }: { id: string; step: StepName; ban?: Ban }) => stepStock(id, step, ban),
    onSuccess: () => client.invalidateQueries({ queryKey: stockKeys.all }),
    onError: (error) => toast.error("Couldn't save that", messageOf(error)),
  });
}

/**
 * Take (a poster, onto their own phone) or Assign (a manager, onto any
 * poster's phone: poster first, then that poster's phone; Jay, 2026-10-08).
 * A manager who is also one of the posters only ever assigns to themselves,
 * so the poster is chosen for them and there is no menu (Jay, 2026-10-19).
 *
 * The creator is one of the active creators in Creators and nothing else:
 * there is no typing a new name here, because Creators is the only place a
 * creator is added (Jay, 2026-10-19). The server refuses any other name too.
 */
export function TakeModal({
  account, phones, creators, rosterKnown = true, photos, posters = [], assign = false, me = null, onClose,
}: { account: Stock | null; phones: Phone[]; creators: string[]; rosterKnown?: boolean; photos: CreatorPhotos; posters?: Poster[]; assign?: boolean; me?: string | null; onClose: () => void }) {
  const self = assign && me && posters.some((p) => p.id === me) ? me : null;
  const [poster, setPoster] = useState<string | null>(self);
  const theirs = useQuery({ queryKey: [...stockKeys.all, "phones-of", poster], queryFn: () => fetchPhonesOf(poster!), enabled: assign && Boolean(poster) });
  const [phone, setPhone] = useState<string | null>(null);
  const [creator, setCreator] = useState<string | null>(null);
  const [extraPhones, setExtraPhones] = useState<string[]>([]);
  const client = useQueryClient();
  const toast = useToast();
  useEffect(() => {
    setPhone(null);
    setCreator(null);
    setPoster(self);
    // A phone typed for the last account is a real phone now, or was never used: either way not "new" here.
    setExtraPhones([]);
  }, [account?.id, self]);
  // A different poster has different phones.
  useEffect(() => setPhone(null), [poster]);
  const personName = self ? "your" : posters.find((p) => p.id === poster)?.name ?? "their";

  const take = useMutation({
    mutationFn: () => takeStock(account!.id, { phone: phone!, creator: creator!, ...(assign ? { poster: poster! } : {}) }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      if (self) toast.success(`@${account!.username} is on your phone ${phone}`, `Start the ${account!.warmup}-day warm-up today.`);
      else if (assign) toast.success(`@${account!.username} is on ${personName}'s phone ${phone}`, `They start the ${account!.warmup}-day warm-up today.`);
      else toast.success(`@${account!.username} is on phone ${phone}`, `Start the ${account!.warmup}-day warm-up today.`);
      onClose();
    },
    onError: (error) => toast.error(assign ? "Couldn't assign it" : "Couldn't take it", messageOf(error)),
  });

  if (!account) return null;
  const phoneList = assign ? theirs.data?.phones ?? [] : phones;
  // Only phones of this account's type are offered: aged and soft reg never share a phone.
  // An empty phone from Create phone has no type yet and takes this account's.
  const fits = (p: Phone) => p.accountType === account.accountType || (p.accountType === "unmarked" && p.accounts === 0);
  const fitting = phoneList.filter(fits);
  const other = phoneList.filter((p) => !fits(p)).map((p) => p.label);
  const phoneOptions = [
    ...fitting.map((p) => ({ value: p.label, label: `Phone ${p.label}`, hint: `${p.accounts} live`, keywords: TYPE_LABEL[p.accountType], badge: <TypeBadge type={p.accountType} mini /> })),
    ...extraPhones
      .filter((label) => !phoneList.some((p) => p.label === label))
      .map((label) => ({ value: label, label: `Phone ${label}`, hint: "New phone", badge: <TypeBadge type={account.accountType} mini /> })),
  ];
  const creatorOptions = [...new Set(creators.map((name) => name.trim().toUpperCase()))].sort().map((name) => ({ value: name, label: name, image: photos(name) }));
  const blocked = phone !== null && other.includes(phone);

  return (
    <Modal
      open
      onClose={onClose}
      title={`${assign ? "Assign" : "Take"} @${account.username}`}
      description={`${TYPE_LABEL[account.accountType]} account from ${account.supplier}. It can only go on ${account.accountType === "aged" ? "an aged" : "a soft reg"} phone.`}
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={(assign && !poster) || !phone || !creator || blocked || take.isPending} onClick={() => take.mutate()}>
            {assign ? (take.isPending ? "Assigning…" : "Assign") : take.isPending ? "Taking…" : "Take it"}
          </button>
        </>
      }
    >
      <div className="igs-take">
        {assign && !self ? (
          <Field label="Poster">
            <Select
              label="Poster"
              value={poster}
              // Exactly the posters ticked in the switcher's list (Jay, 2026-10-08), nobody else.
              options={posters.map((p) => ({ value: p.id, label: p.name }))}
              onChange={setPoster}
              placeholder={posters.length ? "Choose who gets it" : "Tick posters with the cog beside the switcher first"}
              searchable
            />
          </Field>
        ) : null}
        <Field
          label="Phone"
          hint={
            assign && !poster
              ? "Choose the poster first."
              : other.length
                ? `${assign && !self ? `${personName}'s` : "Your"} ${account.accountType === "aged" ? "soft reg" : "aged"} phones (${other.join(", ")}) can't take it.`
                : "Type a new phone's number to add it."
          }
          error={blocked ? `Phone ${phone} is a ${account.accountType === "aged" ? "soft reg" : "aged"} phone.` : undefined}
        >
          <Select
            label="Phone"
            value={phone}
            options={phoneOptions}
            onChange={setPhone}
            placeholder="Choose or add a phone"
            searchable
            disabled={assign && !poster}
            onCreate={(name) => {
              const label = phoneLabel(name);
              if (!label) return;
              setExtraPhones((all) => [...new Set([...all, label])]);
              setPhone(label);
            }}
            createLabel={(name) => `Add phone ${phoneLabel(name)}`}
          />
        </Field>
        <Field label="Creator" hint={rosterKnown ? undefined : "Couldn't read the creators from the Creators tab. Try again in a minute."}>
          <Select
            label="Creator"
            value={creator}
            options={creatorOptions}
            onChange={setCreator}
            placeholder="Choose a creator"
            searchable
            emptyLabel="No active creator matches that."
          />
        </Field>
        <SetupList account={account} />
      </div>
    </Modal>
  );
}

/** The setup Signet picked, read as the poster's to-do list. */
export function SetupList({ account }: { account: Stock }) {
  return (
    <dl className="igs-setup">
      <dt>Account</dt>
      <dd>{classLabel(account)}</dd>
      <dt>Profile picture</dt>
      <dd>{pfpLabel(account.pfp)}</dd>
      <dt>Warm-up</dt>
      <dd>{account.warmupSkipped ? "Not needed, added by hand" : `${account.warmup}-day SOP`}</dd>
      <dt>Bio</dt>
      <dd>{account.bio ? <CopyButton value={account.bio} label="bio" className="igs-copy">{account.bio}</CopyButton> : <span className="faint">No bio set</span>}</dd>
    </dl>
  );
}

/** Fixing a wrong username or password (Jay, 2026-10-19). The rest of the login stays. */
function EditLoginModal({ account, login, onClose }: { account: Stock; login: Login; onClose: () => void }) {
  const [username, setUsername] = useState(login.username);
  const [password, setPassword] = useState(login.password ?? "");
  const client = useQueryClient();
  const toast = useToast();
  const clean = username.trim().replace(/^@/, "").toLowerCase();
  const valid = /^[a-z0-9._]{1,30}$/.test(clean);
  const changed = clean !== login.username || password !== (login.password ?? "");
  const save = useMutation({
    mutationFn: () => saveLogin(account.id, { ...(clean !== login.username ? { username: clean } : {}), ...(password !== (login.password ?? "") ? { password: password || null } : {}) }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success(`Saved @${clean}'s login`, clean !== login.username ? `It was @${login.username}. The next weekly read looks it up under the new name.` : undefined);
      onClose();
    },
    onError: (error) => toast.error("Couldn't save the login", messageOf(error)),
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit @${login.username}'s login`}
      description="Only the username and password change. The email, its password and the 2FA key stay as they are."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={!valid || !changed || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="igs-take">
        <Field label="Username" error={username.trim() && !valid ? "Letters, numbers, dots and underscores, up to 30." : undefined}>
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" spellCheck={false} autoFocus />
        </Field>
        <Field label="Password" hint={password ? undefined : "Leave it empty if there is no password."}>
          <input value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Moving an account to another creator (Jay, 2026-10-28). Only the active
 * creators in Creators are offered, as on Take, and the server checks again.
 */
function ChangeCreatorModal({ account, photos, onClose }: { account: Stock; photos: CreatorPhotos; onClose: () => void }) {
  const [creator, setCreator] = useState<string | null>(null);
  const roster = useQuery({ queryKey: [...stockKeys.all, "creators"], queryFn: fetchStockCreators, staleTime: 60_000 });
  const client = useQueryClient();
  const toast = useToast();
  const save = useMutation({
    mutationFn: () => saveCreator(account.id, creator!),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success(`@${account.username} is ${creator}'s now`, account.creator ? `It was ${account.creator}'s.` : undefined);
      onClose();
    },
    onError: (error) => toast.error("Couldn't change the creator", messageOf(error)),
  });
  const options = [...new Set((roster.data?.creators ?? []).map((name) => name.trim().toUpperCase()))]
    .filter((name) => name !== account.creator)
    .sort()
    .map((name) => ({ value: name, label: name, image: photos(name) }));
  return (
    <Modal
      open
      onClose={onClose}
      title={`Change @${account.username}'s creator`}
      description={account.linkUrl ? "Its bio link still goes to the old creator's link until it gets a new one." : undefined}
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="button brass" disabled={!creator || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="igs-take">
        <Field label="Creator" hint={roster.data && !roster.data.known ? "Couldn't read the creators from the Creators tab. Try again in a minute." : undefined}>
          <Select
            label="Creator"
            value={creator}
            options={options}
            onChange={setCreator}
            placeholder={roster.isLoading ? "Loading…" : "Choose a creator"}
            searchable
            emptyLabel="No active creator matches that."
          />
        </Field>
      </div>
    </Modal>
  );
}

function CreatorBlock({ account, photos, canEdit }: { account: Stock; photos: CreatorPhotos; canEdit: boolean }) {
  const [changing, setChanging] = useState(false);
  return (
    <>
      <div className="igs-creator-line">
        {account.creator ? <CreatorChip name={account.creator} photos={photos} /> : <span className="faint">No creator yet.</span>}
        {canEdit ? (
          <button type="button" className="icon-button ghost small igs-cs-edit" onClick={() => setChanging(true)} aria-label={`Change @${account.username}'s creator`} title="Change creator">
            <EditIcon size={14} />
          </button>
        ) : null}
      </div>
      {changing ? <ChangeCreatorModal account={account} photos={photos} onClose={() => setChanging(false)} /> : null}
    </>
  );
}

function LoginBlock({ account }: { account: Stock }) {
  const { can } = useMe();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const login = useQuery({ queryKey: ["igsuicide", "stock", "login", account.id], queryFn: () => fetchLogin(account.id), enabled: open, staleTime: 0, gcTime: 0 });
  const code = useQuery({
    queryKey: ["igsuicide", "stock", "code", account.id],
    queryFn: () => fetchCode(account.id),
    enabled: open && account.hasTwoFactor,
    refetchInterval: (q) => Math.max(1, q.state.data?.secondsLeft ?? 30) * 1000,
    gcTime: 0,
  });
  if (!can("igsuicide.login:view")) return null;
  if (!open) {
    return (
      <button type="button" className="secondary-button" onClick={() => setOpen(true)}>
        <EyeIcon size={13} /> Show login
      </button>
    );
  }
  if (login.isLoading) return <p className="faint">Loading…</p>;
  if (login.isError) return <p className="igs-warn-text">{messageOf(login.error)}</p>;
  const l = login.data!;
  const line = (label: string, value: string | null) =>
    value ? (
      <>
        <dt>{label}</dt>
        <dd>
          <CopyButton value={value} label={label.toLowerCase()} className="igs-copy">{value}</CopyButton>
        </dd>
      </>
    ) : null;
  return (
    <>
      <dl className="igs-setup igs-login">
        {line("Username", l.username)}
        {line("Password", l.password)}
        {line("Email", l.email)}
        {line("Email password", l.emailPassword)}
        {line("Backup email", l.backupEmail)}
        {line("Backup email password", l.backupEmailPassword)}
        {l.other.length ? line("Other", l.other.join(" : ")) : null}
        {account.hasTwoFactor ? (
          <>
            <dt>2FA code</dt>
            <dd className="igs-secret">
              {code.data ? (
                <>
                  <CopyButton value={code.data.code} label="2FA code" className="igs-copy">
                    <span className="igs-code tabular">{code.data.code}</span>
                  </CopyButton>
                  <small className="faint">{code.data.secondsLeft}s left</small>
                </>
              ) : (
                <span className="faint">…</span>
              )}
            </dd>
          </>
        ) : null}
      </dl>
      {can("igsuicide.stock:edit") ? (
        <div className="igs-actions-row">
          <button type="button" className="secondary-button" onClick={() => setEditing(true)}>
            <EditIcon size={13} /> Edit username or password
          </button>
        </div>
      ) : null}
      {editing ? <EditLoginModal account={account} login={l} onClose={() => setEditing(false)} /> : null}
    </>
  );
}

/**
 * canAct: the account is on the viewer's own phone, or they manage the tab.
 * Everyone may look at every account (Jay, 2026-10-08); only those may log
 * in to it or change it, as the server also insists.
 */
export function AccountDrawer({ account, all = [], manages, canAct = true, onClose, onOpen }: { account: Stock | null; all?: Stock[]; manages: boolean; canAct?: boolean; onClose: () => void; onOpen?: (id: string) => void }) {
  const step = useStep();
  const { can } = useMe();
  const client = useQueryClient();
  const photos = useCreatorPhotos(STOCK);
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const release = useMutation({
    mutationFn: (id: string) => releaseStock(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      toast.success(`@${account?.username} is back in Free to take`, "Its setup and bio stay; its ticks and dates are cleared.");
      onClose();
    },
    onError: (error) => toast.error("Couldn't put it back", messageOf(error)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteStock(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: stockKeys.all });
      onClose();
    },
    onError: (error) => toast.error("Couldn't remove it", messageOf(error)),
  });
  if (!account) return null;
  const primary = PRIMARY[account.step.stage];
  const ended = account.status === "banned" || account.status === "suspended";
  const dates: [string, string | null][] = [
    ["On a phone", account.takenOn],
    ["Warm-up done", account.warmupDoneOn],
    ["Link added", account.linkAddedOn],
    ["Link flagged", account.linkFlaggedOn],
    ["Moved to a highlight", account.highlightOn],
    [account.status === "active" ? "Status" : account.status[0]!.toUpperCase() + account.status.slice(1), account.status === "active" ? (account.pausedOn ? "Not posting" : "Active") : account.statusOn],
    ...(account.pausedOn ? ([["Not posting since", account.pausedOn]] as [string, string | null][]) : []),
  ];

  return (
    <Drawer
      open
      narrow
      onClose={onClose}
      title={`@${account.username}`}
      head={<TypeChip type={account.accountType} />}
      subtitle={
        <>
          {[account.phone ? `${account.offPhone ? "Was on " : ""}${account.posterName ? `${account.posterName}'s ` : ""}${account.offPhone ? "phone" : "Phone"} ${account.phone}` : "Not on a phone", TYPE_LABEL[account.accountType]].join(", ")}
          {account.creator ? <>, <CreatorChip name={account.creator} photos={photos} size={14} /></> : null}
        </>
      }
    >
      {element}
      <div className="igs-drawer">
        <section>
          <h3>{account.step.title}</h3>
          <p className="faint">{account.step.detail}</p>
          {account.missingSince ? <MissingAlert a={account} readOnly={!canAct} /> : null}
          {ended ? <p className="igs-banned-line">{banLine(account)}</p> : null}
          <RemovedPrompt a={account} canAct={canAct} undo />
          {ended && account.causedBy && onOpen ? (
            <p className="faint">
              Connected to <button type="button" className="igs-inline-link" onClick={() => onOpen(account.causedBy!.id)}>@{account.causedBy.username}</button>. It comes back if that one does.
            </p>
          ) : null}
          {!canAct ? <p className="faint">On {account.posterName ?? "another poster"}'s phone. Only they or a manager can log in to it or change it.</p> : null}
          {canAct ? (
          <div className="igs-actions-row">
            {account.phone ? <StatusButtons a={account} /> : null}
            {primary && account.phone ? (
              <button type="button" className="button brass" disabled={step.isPending} onClick={() => step.mutate({ id: account.id, step: primary.step })}>
                {primary.label}
              </button>
            ) : null}
          </div>
          ) : null}
        </section>

        {isThreeReason(account) ? <AppealPanel account={account} all={all} canAct={canAct} onOpen={(id) => onOpen?.(id)} /> : null}

        <section>
          <h3>Note</h3>
          <AccountNote a={account} canEdit={canAct} />
        </section>

        {account.creator || account.phone ? (
          <section>
            <h3>Creator</h3>
            <CreatorBlock account={account} photos={photos} canEdit={canAct && can("igsuicide.stock:edit")} />
          </section>
        ) : null}

        <section>
          <h3>Setup</h3>
          <SetupList account={account} />
        </section>

        {account.phone && canAct ? (
          <section>
            <h3>Login</h3>
            <LoginBlock account={account} />
          </section>
        ) : null}

        <section>
          <h3>Dates</h3>
          <dl className="igs-setup">
            {dates.map(([label, value]) => (
              <div key={label} className="igs-date">
                <dt>{label}</dt>
                <dd className="tabular">{value ? (/^\d{4}-\d{2}-\d{2}$/.test(value) ? shortDate(value) : value) : <span className="faint">Not yet</span>}</dd>
              </div>
            ))}
            <dt>Links flagged</dt>
            <dd className="tabular">{account.flagCount ? `${account.flagCount} ${account.flagCount === 1 ? "time" : "times"}` : <span className="faint">Never</span>}</dd>
            <dt>Followers</dt>
            <dd className="tabular">{account.followers ?? <span className="faint">Not read yet</span>}</dd>
            <dt>Reel views</dt>
            <dd className="tabular">{account.views ?? <span className="faint">Not read yet</span>}</dd>
          </dl>
          <p className="faint igs-small">
            <a href={account.link} target="_blank" rel="noreferrer noopener">Supplier link</a>
          </p>
        </section>

        {account.phone && can("igsuicide.stock:delete") ? (
          <section>
            <h3>Admin</h3>
            <p className="faint">Takes it off {account.phone ? `phone ${account.phone}` : "its phone"} and puts it back in Free to take for anyone to take again. Its ticks and dates are cleared.</p>
            <button
              type="button"
              className="secondary-button"
              disabled={release.isPending}
              onClick={async () => {
                if (await confirm({ title: `Put @${account.username} back in Free to take?`, body: `It comes off ${account.phone ? `phone ${account.phone}` : "its phone"}, and its ticks and dates are cleared. Its setup and bio stay.`, confirmLabel: "Put it back" })) release.mutate(account.id);
              }}
            >
              Remove from this phone
            </button>
          </section>
        ) : null}

        {manages && can("igsuicide.stock:delete") ? (
          <section>
            <button
              type="button"
              className="secondary-button compact igs-danger"
              onClick={async () => {
                if (await confirm({ title: `Remove @${account.username}?`, body: "Its login, steps and readings go with it. Use this for a bad upload, not for a banned account.", confirmLabel: "Remove", danger: true })) remove.mutate(account.id);
              }}
            >
              <TrashIcon size={13} /> Remove account
            </button>
          </section>
        ) : null}
      </div>
    </Drawer>
  );
}

